// ─────────────────────────────────────────────────────────────
// CommonSidebar — the shared dark-navy left navigation.
//
// This is the reusable version of the main dashboard sidebar
// (`src/Components/Sidebar.js`). It renders with the SAME class names
// and stylesheet (`src/styles/Sidebar.scss`) so the look — navy
// surface, white nav items, hover/active states, spacing — is
// identical everywhere it's used. Only the *content* is dynamic, so
// any portal (AI Adoption Portal, etc.) can drop it in with its own
// nav tree.
//
// Two navigation modes:
//   mode="button"  → state-driven (activeId + onSelect)  ← portals
//   mode="link"    → route-driven (react-router NavLink)  ← main app
//
// Props:
//   groups     [{ label, items: [{ id, to, title|label, icon, svgIcon, subtitle, badge }] }]
//   mode       'button' (default) | 'link'
//   activeId   currently-selected item id            (button mode)
//   onSelect   (id) => void                           (button mode)
//   collapsed  narrow rail — hides labels, centres icons
//   onToggle   if provided, renders the collapse chevron
//   header     node rendered at the top (brand / logo block)
//   footer     node rendered at the bottom (e.g. upgrade card)
//   isMobileOpen / onClose  mobile drawer close button
//   className / style / ariaLabel   passthrough on the <nav>
// ─────────────────────────────────────────────────────────────

import React from 'react';
import { NavLink } from 'react-router-dom';
import '../styles/Sidebar.scss';
import foreseeLogo from '../NovaWidget/assets/foresee-eagle.png';

// Default brand mark — the NMT shield, same asset the main dashboard
// sidebar uses. Shown at the top of every CommonSidebar by default.
// const NMT_LOGO = 'https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/small_logo.svg';

// Red count badge — mirrors the inline badge the portal rail used, so
// the "pending reviews" pill looks the same in the navy sidebar.
const badgeStyle = {
    marginLeft: 'auto',
    background: 'var(--color-danger, #D24646)',
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    borderRadius: 10,
    padding: '1px 7px',
    minWidth: 18,
    textAlign: 'center',
    lineHeight: 1.4,
};

// A <button> needs a few resets to match the <a class="navItem"> look
// (Sidebar.scss styles the class, but buttons carry UA font/border).
const buttonReset = {
    border: 'none',
    width: '100%',
    font: 'inherit',
    background: 'transparent',
    textAlign: 'left',
};

export default function CommonSidebar({
    groups = [],
    mode = 'button',
    activeId,
    onSelect,
    collapsed = false,
    onToggle,
    brandTitle = 'Foresee Security',
    subtitle = '',      // tab / section label under the brand (e.g. "AI Adoption")
    logoSrc = foreseeLogo,
    header,             // pass to fully override the default NMT brand header
    footer = null,
    isMobileOpen = false,
    onClose,
    className = '',
    style,
    ariaLabel = 'Sidebar',
}) {
    const renderItem = (item) => {
        const label = item.title ?? item.label;
        const badge = Number(item.badge) || 0;
        const inner = (
            <>
                {item.svgIcon
                    ? item.svgIcon
                    : item.icon
                        ? <img src={item.icon} alt={label} className="icon" height={17} width={16} />
                        : null}
                {!collapsed && <span className="label">{label}</span>}
                {badge > 0 && <span style={badgeStyle}>{badge}</span>}
            </>
        );

        if (mode === 'link') {
            // An item may force its own active state (`item.active`) — needed when
            // several links share a pathname but differ by query string (e.g.
            // /dashboard?view=easm vs ?view=compliance), which NavLink can't tell
            // apart on its own. Falls back to NavLink's route match otherwise.
            return (
                <NavLink
                    key={item.to ?? item.id}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => ((item.active !== undefined ? item.active : isActive) ? 'navItem active' : 'navItem')}
                    title={item.subtitle}
                    onClick={onClose}
                >
                    {inner}
                </NavLink>
            );
        }

        const isActive = item.id === activeId;
        return (
            <button
                key={item.id}
                type="button"
                className={`navItem ${isActive ? 'active' : ''}`}
                style={buttonReset}
                onClick={() => onSelect && onSelect(item.id)}
                title={item.subtitle}
            >
                {inner}
            </button>
        );
    };


    return (
        <nav
            className={`sidebar common-sidebar ${collapsed ? 'collapsed' : ''} ${className}`.trim()}
            style={style}
            aria-label={ariaLabel}
        >
            {isMobileOpen && (
                <button className="closeButton" onClick={onClose} aria-label="Close menu">✕</button>
            )}

            {/* Brand header — NMT shield by default; `header` prop overrides.
                Collapsed shows just the mark, matching the dashboard sidebar. */}
            {header !== undefined ? header : (
                collapsed ? (
                    <div className="logoContainer">
                        <img src={logoSrc} alt={brandTitle} height={50} width={50} />
                    </div>
                ) : (
                    <div className="logoContainer">
                        <div className="d-flex align-items-center gap-2">
                            <img src={logoSrc} alt={brandTitle} height={44} width={44} />
                            <div className="sb-brand-text">
                                <span className="sb-brand-name">FORESEE</span>
                                <span className="sb-brand-sub">SECURITY</span>
                            </div>
                        </div>
                    </div>
                )
            )}

            {onToggle && (
                <button
                    className="sidebar-toggle-btn"
                    onClick={onToggle}
                    aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        {collapsed
                            ? <polyline points="9 18 15 12 9 6" />
                            : <polyline points="15 18 9 12 15 6" />}
                    </svg>
                </button>
            )}

            <div>
                {groups.map((group, gi) => (
                    <div key={group.label || gi}>
                        {!collapsed && group.label && <p className="platform">{group.label}</p>}
                        {group.items.map(renderItem)}
                    </div>
                ))}
            </div>

            {footer}
        </nav>
    );
}
